import type { SerializedNode } from '@craftjs/core';

import {
  ALLOWED_TYPES,
  BLOCK_DISPLAY_NAMES,
  type CommonBlock,
  type CommonData,
  type EditableBlocks,
} from './blockEditor.types';

const DEFAULT_CHILD_KEYS = new Set(['default', 'children']);

// Components that are canvases (drop zones) in Craft.js.
// Most layout components (ListItemsSection, TwoColumnLayout) are NOT canvases -
// they create canvas children via <Element>.
const CANVAS_COMPONENTS = new Set<string>([]);

// Layout components that use linkedNodes for ALL their children (including 'default').
// These components render <Element id="slotName"> for each slot.
const LAYOUT_COMPONENTS_WITH_LINKED_CHILDREN = new Set([
  'ListItemsSection',
  'TwoColumnLayout',
  'EditableCallout',
]);

// Map block types to their preferred canvas container for linkedNodes
// Consumers can extend this via custom transformer configuration
const LINKED_NODE_CONTAINER_MAP: Record<string, string> = {
  // TwoColumnLayout's columns use BlockEditorCanvasContainer (generic)
  TwoColumnLayout: 'BlockEditorCanvasContainer',
  // ListItemsSection's default slot uses BlockEditorCanvasContainer (generic)
  ListItemsSection: 'BlockEditorCanvasContainer',
  // EditableCallout's content uses CalloutCanvasContainer (no nested callouts)
  EditableCallout: 'CalloutCanvasContainer',
};

const DEFAULT_LINKED_NODE_CONTAINER = 'BlockEditorCanvasContainer';

interface ContainerConfig {
  resolvedName: string;
  displayName: string;
  custom?: Record<string, unknown>;
}

const CONTAINER_CONFIG_MAP: Record<string, ContainerConfig> = {
  BlockEditorCanvasContainer: {
    resolvedName: 'BlockEditorCanvas',
    displayName: 'Content',
    custom: {
      allowedTypes: ALLOWED_TYPES.containerContent,
    },
  },
  CalloutCanvasContainer: {
    resolvedName: 'BlockEditorCanvas',
    displayName: 'Callout Content',
    custom: {
      allowedTypes: ALLOWED_TYPES.calloutContent,
    },
  },
};

function toDisplayName(type: string): string {
  return (
    (BLOCK_DISPLAY_NAMES as Record<string, string>)[type] ??
    type
      .replace(/([A-Z])/g, ' $1')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

const ROOT_COMPONENT = 'BlockEditorRoot';

interface CanvasContainerOptions {
  parentId: string;
  childIds: string[];
  containerType: string;
  /** Props to pass to the container (e.g., sx from parent ListItemsSection) */
  containerProps?: Record<string, unknown>;
}

function createCanvasContainer({
  parentId,
  childIds,
  containerType,
  containerProps = {},
}: CanvasContainerOptions): SerializedNode {
  const config =
    CONTAINER_CONFIG_MAP[containerType] ??
    CONTAINER_CONFIG_MAP[DEFAULT_LINKED_NODE_CONTAINER]!;

  const customProps: Record<string, unknown> = { ...(config.custom || {}) };

  return {
    type: {
      resolvedName: config.resolvedName,
    },
    isCanvas: true,
    props: containerProps,
    displayName: config.displayName,
    custom: customProps,
    parent: parentId,
    hidden: false,
    nodes: childIds,
    linkedNodes: {},
  };
}

export function transformCommonDataToCraft(
  commonData: CommonData,
  rootNodeId: string = 'ROOT',
  rootType: string = ROOT_COMPONENT,
  rootProps: Record<string, unknown> = {},
  useLinkedNodesForRootChildren: boolean = false
): EditableBlocks {
  const craftData: EditableBlocks = {};
  const rootNodes: string[] = [];
  const rootLinkedNodes: Record<string, string> = {};

  function processBlock(block: CommonBlock, parentId: string | null): string {
    const { id, type, props, children } = block;
    const displayName = toDisplayName(type);

    // Build custom data for the node
    const customData: Record<string, unknown> = {};

    const craftNode: SerializedNode = {
      type: {
        resolvedName: type,
      },
      isCanvas: CANVAS_COMPONENTS.has(type),
      props: { ...props },
      displayName,
      custom: customData,
      parent: parentId,
      hidden: false,
      nodes: [],
      linkedNodes: {},
    };

    craftData[id] = craftNode;

    if (!children) {
      return id;
    }

    const childRecord = children as Record<string, CommonBlock[]>;

    Object.keys(childRecord).forEach((slot) => {
      const slotChildren = childRecord[slot];
      if (!slotChildren || slotChildren.length === 0) {
        return;
      }

      // Layout components with linked children (like ListItemsSection, TwoColumnLayout)
      // always use linkedNodes, even for 'default' slot
      const usesLinkedNodes = LAYOUT_COMPONENTS_WITH_LINKED_CHILDREN.has(type);

      if (DEFAULT_CHILD_KEYS.has(slot) && !usesLinkedNodes) {
        // Simple canvas component - children go directly in nodes array
        craftNode.isCanvas = true;
        slotChildren.forEach((child) => {
          const childId = processBlock(child, id);
          craftNode.nodes.push(childId);
        });
        return;
      }

      // Layout component or named slot - create a linkedNode with a canvas container
      const containerId = `${id}-${slot}`;

      const childIds = slotChildren.map((child) =>
        processBlock(child, containerId)
      );
      craftNode.linkedNodes[slot] = containerId;

      // Use the configured container type for this block, or fall back to default
      const containerType =
        LINKED_NODE_CONTAINER_MAP[type] ?? DEFAULT_LINKED_NODE_CONTAINER;

      // Pass sx from parent to container (for ListItemsSection gap customization)
      const containerProps: Record<string, unknown> = {};
      if (props?.sx !== undefined) {
        containerProps.sx = props.sx;
      }

      craftData[containerId] = createCanvasContainer({
        parentId: id,
        childIds,
        containerType,
        containerProps,
      });
    });

    return id;
  }

  for (const block of commonData.blocks) {
    const blockId = processBlock(block, rootNodeId);
    if (useLinkedNodesForRootChildren) {
      // Use the block ID as the slot name for linked nodes
      rootLinkedNodes[block.id] = blockId;
    } else {
      rootNodes.push(blockId);
    }
  }

  craftData[rootNodeId] = {
    type: {
      resolvedName: rootType,
    },
    // When using linked nodes for root children, the root itself is not a canvas.
    // This prevents the root from showing as a drop zone (which would cause dropped items to disappear).
    isCanvas: !useLinkedNodesForRootChildren,
    props: rootProps,
    displayName: 'Root',
    custom: {
      // Only set allowedTypes if root is a canvas (accepts direct children)
      ...(useLinkedNodesForRootChildren
        ? {}
        : { allowedTypes: ALLOWED_TYPES.layoutContainers }),
    },
    parent: null,
    hidden: false,
    nodes: rootNodes,
    linkedNodes: rootLinkedNodes,
  };

  return craftData;
}
